import React from 'react';
import { usePeersonaResourcesPanel } from '../../hooks/api/usePeersonaResources';
import { PeersonaIntroVideo } from './peersona-intro-video';
import { PeersonaResourcesPanel } from './peersona-resources-panel';
import { PeersonaSparkle } from './peersona-sparkle';
import { PEERSONA_CHIP_BG, PEERSONA_DIVIDER, PEERSONA_TEXT_SECONDARY, PEERSONA_TEXT_TERTIARY } from './peersona-styles';

/**
 * Persistent welcome hero. The video/greeting/scope block scrolls with
 * the conversation; the action strip (and optional resources dropdown)
 * below it pins to the top via ``position: sticky`` once the hero scrolls past.
 *
 * Rendered as a fragment (not a <section>) on purpose: sticky elements
 * are bounded by their parent's height, so wrapping both children in a
 * section would unstick the strip as soon as the hero left the viewport.
 * As fragment children, the strip's parent is the scroll container, which
 * spans the full message list — so the strip stays pinned for any scroll
 * depth.
 */
interface PeersonaWelcomeHeroProps {
  peerName: string;
  avatar?: string | null;
  scopeLine?: string | null;
  welcomeVideoVimeoId?: string | null;
  onLearnMore?: () => void;
}

export const PeersonaWelcomeHero: React.FC<PeersonaWelcomeHeroProps> = ({
  peerName,
  avatar,
  scopeLine,
  welcomeVideoVimeoId,
  onLearnMore,
}) => {
  const {
    open: resourcesOpen,
    toggle: toggleResources,
    close: closeResources,
    resources,
    isLoading,
    isResorting,
  } = usePeersonaResourcesPanel();

  return (
    <>
      <div
        aria-label={`Welcome from ${peerName}`}
        role="region"
        className="flex flex-col items-center px-4 pb-4 pt-8 text-center"
      >
        <PeersonaIntroVideo peerName={peerName} welcomeVideoVimeoId={welcomeVideoVimeoId} avatar={avatar} />
        <div className="font-albert-sans mt-6 inline-flex items-center gap-2 text-lg font-semibold text-black">
          <span>Hi, I&rsquo;m</span>
          <span
            className="inline-flex items-center gap-2 rounded-lg px-2 py-1"
            style={{ backgroundColor: PEERSONA_CHIP_BG }}
          >
            <PeersonaSparkle size={15} style={{ color: PEERSONA_TEXT_SECONDARY }} />
            <span>{peerName}</span>
          </span>
        </div>
        {scopeLine ? (
          <p
            className="font-open-sans mt-4 max-w-[28rem] text-base leading-snug"
            style={{ color: PEERSONA_TEXT_SECONDARY }}
          >
            {scopeLine}
          </p>
        ) : null}
      </div>
      {/* Sticky strip + resources dropdown — pinned once the hero scrolls past. */}
      <div
        className="sticky top-0 z-[2] bg-white"
        style={{
          borderTop: `1px solid ${PEERSONA_DIVIDER}`,
          borderBottom: `1px solid ${PEERSONA_DIVIDER}`,
        }}
      >
        {onLearnMore ? (
          <div className="flex divide-x" style={{ borderColor: PEERSONA_DIVIDER }}>
            <button
              type="button"
              onClick={onLearnMore}
              className="font-open-sans flex flex-1 items-center justify-center gap-1 bg-white py-2.5 text-sm font-semibold transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300"
              style={{ color: PEERSONA_TEXT_TERTIARY }}
            >
              <span>Learn more about {peerName}</span>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path
                  d="M9 6l6 6-6 6"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={toggleResources}
              aria-expanded={resourcesOpen}
              className="font-open-sans flex flex-1 items-center justify-center gap-1 bg-white py-2.5 text-sm font-semibold transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300"
              style={{ color: PEERSONA_TEXT_TERTIARY }}
            >
              <span>View organizational resources</span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
                className={resourcesOpen ? 'rotate-180' : ''}
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={toggleResources}
            aria-expanded={resourcesOpen}
            className="font-open-sans flex w-full items-center justify-center gap-1 bg-white py-2.5 text-sm font-semibold transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-300"
            style={{ color: PEERSONA_TEXT_TERTIARY }}
          >
            <span>View organizational resources</span>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden
              className={resourcesOpen ? 'rotate-180' : ''}
            >
              <path
                d="M6 9l6 6 6-6"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        )}
        <PeersonaResourcesPanel
          open={resourcesOpen}
          resources={resources}
          isLoading={isLoading}
          isResorting={isResorting}
          onClose={closeResources}
        />
      </div>
    </>
  );
};
